import { of as observableOf, from as observableFrom, Subject, timer, EMPTY } from "rxjs";
import { first, mergeMap, debounce } from "rxjs/operators";
import { USERINPUTTYPE } from "../../userinput/internal";
import { GeolocationLayer } from "./GeolocationLayer";
import { register } from "ol/proj/proj4";
import Olmap from "ol/Map";
import Zoom from "ol/control/Zoom";
import OSM from "ol/source/OSM";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS";
import TileLayer from "ol/layer/Tile";
import View from "ol/View";
import Point from "ol/geom/Point";
import MouseWheelZoomInteraction from "ol/interaction/MouseWheelZoom";
import DragPanInteraction from "ol/interaction/DragPan";
import proj4 from "proj4";
import { isDefined } from "../../../infra3d/infra3d";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import TileWMS from "ol/source/TileWMS";
import OlParser from "geostyler-openlayers-parser";
import LayerGroup from "ol/layer/Group";
import LayerSwitcher from "./LayerSwitcher";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import { GeoCoords } from "../../geo/GeoCoords";
import OlStyle from "ol/style/Style";
import { Fill, Stroke, Circle } from "ol/style";
import Overlay from "ol/Overlay";
import Style from "ol/style/Style";
import { add_tooltip_to_olcontrol } from "./util";
import LegendControl from "./LegendControl";
import { FramesLayer as FramesLayerV2 } from "./framer_v2/FramesLayer";
import { FramesLayer as FramesLayerV3 } from "./framer_v3/FramesLayer";
import { RoutesLayer as RoutesLayerV3 } from "./framer_v3/RoutesLayer";
import { RoutesLayer as RoutesLayerV2 } from "./framer_v2/RoutesLayer";
import { LidarLayer } from "./LidarLayer";
import { Infra3DDataProviderBase } from "../../api/Infra3DDataProviderBase";
import { PinchZoom } from "ol/interaction";
export class Map {
    constructor(container, mapContainer, navigator, mapConfiguration) {
        this._map = null;
        this._scene = null;
        this._navigator = null;
        this._tooltip_overlay = null;
        this._geolocationlayer = null;
        this._local_node_layer = null;
        this._routes_layer = null;
        this._frames_layer = null;
        this._lidar_layer = null;
        this._layerSwitcher = null;
        this._navigator = navigator;
        this._scene = container.scene;
        register(proj4);
        let olstyle_frames = (feature, resolution) => {
            const stroke = new Stroke({
                color: "rgba(13, 146, 210, 0.8)",
                width: 4,
            });
            const fill = new Fill({
                color: "rgba(13, 146, 210, 0.8)",
            });
            const defaultstyle = new OlStyle({
                image: new Circle({
                    fill: fill,
                    radius: 4,
                }),
                stroke: stroke,
                zIndex: 200,
            });
            return defaultstyle;
        };
        let olstyle_area = (feature, resolution) => {
            const defaultstyle = new OlStyle({
                fill: new Fill({
                    color: "rgba(13, 146, 210, 0.5)",
                }),
                zIndex: 200,
            });
            return defaultstyle;
        };
        let olstyle_lines = (feature, resolution) => {
            const defaultstyle = new OlStyle({
                stroke: new Stroke({
                    color: "rgba(13, 146, 210, 0.8)",
                    width: 4,
                }),
                zIndex: 200,
            });
            return defaultstyle;
        };
        let gstyle = null;
        if (mapConfiguration.style) {
            const olParser = new OlParser();
            gstyle = JSON.parse(JSON.stringify(mapConfiguration.style));
            for (const rule of gstyle.rules) {
                rule.symbolizers = rule.symbolizers.filter((x) => x.kind !== "Fill");
            }
            olstyle_frames = olParser.geoStylerStyleToOlParserStyleFct(gstyle);
            const gstyle_area = JSON.parse(JSON.stringify(mapConfiguration.style));
            for (const rule of gstyle_area.rules) {
                rule.symbolizers = rule.symbolizers.filter((x) => x.kind == "Fill");
            }
            if (gstyle_area.rules.map((x) => x.symbolizers.length).every((x) => x == 1))
                olstyle_area = olParser.geoStylerStyleToOlParserStyleFct(gstyle_area);
            const gstyle_line = JSON.parse(JSON.stringify(mapConfiguration.style));
            for (const rule of gstyle_line.rules) {
                rule.symbolizers = rule.symbolizers.filter((x) => x.kind == "Line");
            }
            if (gstyle_line.rules.map((x) => x.symbolizers.length).every((x) => x == 1))
                olstyle_lines = olParser.geoStylerStyleToOlParserStyleFct(gstyle_line);
        }
        const map_element = document.createElement("div");
        map_element.className = "infra3dsdk-map";
        mapContainer.innerHTML = "";
        mapContainer.appendChild(map_element);
        this._geolocationlayer = new GeolocationLayer(this._scene, this._navigator, mapConfiguration.locatorIcon);
        this._map = new Olmap({
            target: map_element,
            controls: [],
            interactions: [new PinchZoom(), new MouseWheelZoomInteraction(), new DragPanInteraction()],
            layers: [this._geolocationlayer.layer],
        });
        this.base_layers(mapConfiguration.baselayers);
        this.overlay_layers(mapConfiguration.overlaylayers);
        this._currentnodesubscription = this._navigator.stateService.currentNode$.subscribe((node) => {
            this._recenterMap(node);
        });
        this._layerSwitcher = new LayerSwitcher({
            reverse: false,
            activationMode: "click",
            collapseLabel: "",
        });
        if (!mapConfiguration.hidecontrols) {
            this._map.addControl(new Zoom());
            this._map.addControl(this._layerSwitcher);
            // add legend as ol-control
            if (gstyle.rules.length > 1) {
                this._map.addControl(new LegendControl({
                    style: gstyle,
                }));
            }
            add_tooltip_to_olcontrol("ol-zoom-in", mapConfiguration.locale.zoomin);
            add_tooltip_to_olcontrol("ol-zoom-out", mapConfiguration.locale.zoomout);
            add_tooltip_to_olcontrol("layer-switcher-button", mapConfiguration.locale.basemaps);
            add_tooltip_to_olcontrol("legend-control-button", mapConfiguration.locale.legend);
            this._map.on("moveend", (evt) => {
                document.getElementsByClassName("ol-zoom-in")[0].blur();
                document.getElementsByClassName("ol-zoom-out")[0].blur();
            });
        }
        let ontooltip;
        if (mapConfiguration.tooltip) {
            const tooltip_element = document.createElement("div");
            tooltip_element.className = "infra3dsdk-tooltip";
            const overlay = new Overlay({
                element: tooltip_element,
                autoPan: {
                    animation: {
                        duration: 250,
                    },
                },
                positioning: "bottom-left",
                offset: [15, 0],
            });
            const tooltip_data$ = new Subject();
            observableFrom(tooltip_data$)
                .pipe(debounce(([coordinate, feature]) => (feature ? timer(500) : EMPTY)))
                .subscribe(([coordinate, feature]) => {
                if (feature) {
                    let tooltip_data = mapConfiguration.tooltip.map((x) => {
                        try {
                            return eval(`feature.getProperties().${x}`);
                        }
                        catch {
                            return "";
                        }
                    });
                    tooltip_data = tooltip_data.filter((x) => x !== "" && x !== undefined);
                    if (tooltip_data.length > 0) {
                        overlay.setPosition(coordinate);
                        overlay.getElement().innerHTML = tooltip_data.reduce((a, r) => `${a}</br>${r}`);
                        overlay.setPositioning("bottom-left");
                    }
                }
                else {
                    overlay.setPosition(undefined);
                }
            });
            ontooltip = (coordinate, feature) => {
                tooltip_data$.next([coordinate, feature]);
            };
            this._map.addOverlay(overlay);
        }
        if (!(this._navigator.api.data instanceof Infra3DDataProviderBase)) {
            return;
        }
        switch (this._navigator.api.data.framer_version) {
            case "2": {
                this._routes_layer = new RoutesLayerV2(this._navigator, this._map, mapConfiguration.overviewlayers, olstyle_lines, olstyle_area, ontooltip, mapConfiguration.overviewsegmentedonly);
                this._frames_layer = new FramesLayerV2(this._navigator, this._map, olstyle_frames, mapConfiguration.campaignstylemode, ontooltip);
                break;
            }
            case "3": {
                const routesconfig = mapConfiguration.routesconfig || {
                    routes: [
                        {
                            loa: 0,
                            min_zoom: 22,
                            max_zoom: 100,
                        },
                    ],
                    route_lines: [
                        {
                            loa: 0,
                            min_zoom: 17,
                            max_zoom: 22,
                        },
                        {
                            loa: 1,
                            min_zoom: 14,
                            max_zoom: 17,
                        },
                    ],
                    route_hexes: [
                        {
                            loa: 0,
                            min_zoom: 12,
                            max_zoom: 14,
                        },
                        {
                            loa: 1,
                            min_zoom: 0,
                            max_zoom: 12,
                        },
                    ],
                };
                // For using the routeslayer also for the hexagons, refactor the more user friendly overview config
                const rout_hex_config = [];
                if (routesconfig.route_lines) {
                    routesconfig.route_lines.forEach((route_line) => {
                        rout_hex_config.push({
                            type: "route_lines",
                            loa: route_line.loa,
                            min_zoom: route_line.min_zoom,
                            max_zoom: route_line.max_zoom,
                        });
                    });
                }
                if (routesconfig.route_hexes) {
                    routesconfig.route_hexes.forEach((route_hex) => {
                        rout_hex_config.push({
                            type: "route_hexes",
                            loa: route_hex.loa,
                            min_zoom: route_hex.min_zoom,
                            max_zoom: route_hex.max_zoom,
                        });
                    });
                }
                this._routes_layer = new RoutesLayerV3(this._navigator, this._map, rout_hex_config, olstyle_lines, olstyle_area);
                if (routesconfig.routes.length !== 1)
                    throw new Error("routesconfig.routes currently only supports one element in the array!");
                this._frames_layer = new FramesLayerV3(this._navigator, this._map, olstyle_frames, routesconfig.routes[0]);
                this._lidar_layer = new LidarLayer(this._navigator, this._map, rout_hex_config, olstyle_lines, olstyle_area);
                break;
            }
        }
        /*         this._add_debug_layer(); */
        this._navigator.stateService.currentNode$.pipe(first()).subscribe((node) => {
            this._init_view(mapConfiguration, node);
        });
    }
    get olmap() {
        return this._map;
    }
    get lidarLayer() {
        return this._lidar_layer;
    }
    resize() {
        this._map.updateSize();
    }
    _init_view(mapConfiguration, startnode) {
        const mapcenter = new Point([startnode.latLon.lon, startnode.latLon.lat]);
        mapcenter.transform("EPSG:4326", "EPSG:3857");
        this._map.setView(new View({
            center: mapcenter.getCoordinates(),
            zoom: mapConfiguration.startupzoom || 15,
        }));
    }
    async createTileLayer(layerconfig, type) {
        const commonProps = {
            title: layerconfig.name,
            type,
            icon: layerconfig.icon,
            opacity: layerconfig.opacity,
            minResolution: layerconfig.minResolution,
            maxResolution: layerconfig.maxResolution,
            zIndex: layerconfig.zIndex,
            visible: isDefined(layerconfig.visible) ? layerconfig.visible : true,
        };
        switch (layerconfig.type) {
            case "OSM":
                return new TileLayer({
                    ...commonProps,
                    source: new OSM({
                        ...layerconfig.options,
                    }),
                });
            case "WMTS":
                if (layerconfig.capabilities !== "") {
                    const r = await fetch(layerconfig.capabilities);
                    const text = await r.text();
                    const result = new WMTSCapabilities().read(text);
                    return new TileLayer({
                        ...commonProps,
                        source: new WMTS({
                            ...layerconfig.options,
                            ...optionsFromCapabilities(result, { layer: layerconfig.options.layer }),
                        }),
                    });
                }
                layerconfig.options["tileGrid"] = new WMTSTileGrid(layerconfig.tilegridoptions);
                return new TileLayer({ ...commonProps, source: new WMTS(layerconfig.options) });
            case "WMS":
                return new TileLayer({ ...commonProps, source: new TileWMS(layerconfig.options) });
            default:
                return null;
        }
    }
    async populateLayerGroup(group, layerconfigs, type) {
        for (let i = layerconfigs.length - 1; i >= 0; i--) {
            if (!("zIndex" in layerconfigs[i]))
                layerconfigs[i].zIndex = group.getZIndex() + (layerconfigs.length - 1 - i);
        }
        const layers = await Promise.all(layerconfigs.map((layerconfig) => this.createTileLayer(layerconfig, type)));
        for (const layer of layers) {
            if (layer) {
                this._map.addLayer(layer);
                group.getLayers().push(layer);
            }
        }
    }
    async base_layers(baselayers = [{ type: "OSM" }]) {
        const group = new LayerGroup({ title: "Base maps", layers: [], zIndex: 0 });
        await this.populateLayerGroup(group, baselayers, "base");
        return group;
    }
    async overlay_layers(overlaylayers = []) {
        const group = new LayerGroup({ title: "Overlays", layers: [], zIndex: 100 });
        await this.populateLayerGroup(group, overlaylayers, "overlay");
        return group;
    }
    _add_debug_nodes(nodes) {
        for (const feature of this._local_node_layer.getSource().getFeatures()) {
            this._local_node_layer.getSource().removeFeature(feature);
        }
        for (const node of nodes) {
            this._local_node_layer.getSource().addFeature(new Feature({
                geometry: new Point([node.latLon.lon, node.latLon.lat]).transform("EPSG:4326", "EPSG:3857"),
                campaignID: node.frame.campaignID,
            }));
        }
        /* const group_sequences = nodes.reduce((r, a) => {
            r[a.frame.stream] = [...(r[a.frame.stream] || []), a];
            return r;
        }, {});
    
        for (const sequence_key in group_sequences) {
            const sequence = group_sequences[sequence_key];
            sequence.sort((a, b) => a.frame.framenumber - b.frame.framenumber);
    
            const line_coords = sequence.map((x) => [x.latLon.lon, x.latLon.lat]);
            this._local_node_layer.getSource().addFeature(
                new Feature({
                    geometry: new LineString(line_coords).transform("EPSG:4326", "EPSG:3857"),
                    campaignID: sequence[0].frame.campaignID,
                })
            );
        } */
    }
    add_infra3d_vectorlayer(mylayer) {
        const vectorLayer = new VectorLayer({
            map: this._map,
            source: new VectorSource({
                format: new GeoJSON({
                    featureProjection: "EPSG:3857",
                    dataProjection: "EPSG:2056",
                }),
            }),
            style: new Style({
                stroke: new Stroke({
                    color: "red",
                    width: 1.25,
                }),
            }),
            zIndex: 1000,
        });
        mylayer.scene.userInputHandler.subscribeEvent(USERINPUTTYPE["LAYERUPDATED"], (layer, updates) => {
            if (layer == mylayer) {
                if (Object.prototype.hasOwnProperty.call(updates, "added") &&
                    Object.prototype.hasOwnProperty.call(updates, "updated"))
                    if (updates["added"].length > 0 || updates["updated"].length > 0) {
                        const geojson = JSON.parse(layer.featurecollection.toGeoJsonString());
                        const features = vectorLayer.getSource().getFormat().readFeatures(geojson);
                        vectorLayer.getSource().clear();
                        vectorLayer.getSource().addFeatures(features);
                    }
            }
        }, this);
    }
    _add_debug_layer() {
        const style = new Style({
            image: new Circle({
                fill: new Fill({
                    color: "rgba(255, 0, 0, 1.0)",
                }),
                stroke: new Stroke({
                    color: "rgba(255, 0, 0, 1.0)",
                    width: 1.0,
                }),
                radius: 6,
            }),
            zIndex: 0,
        });
        const local_node_layer = new VectorLayer({
            zIndex: 150,
            map: this._map,
            style: style,
            source: new VectorSource(),
            minZoom: 20,
        });
        this._local_node_layer = local_node_layer;
        const geoCoords = new GeoCoords();
        this._navigator.stateService.currentNode$
            .pipe(mergeMap((node) => {
            const sw = geoCoords.enuToGeodetic(-20, -20, 0, node.latLon.lat, node.latLon.lon, 0);
            const ne = geoCoords.enuToGeodetic(20, 20, 0, node.latLon.lat, node.latLon.lon, 0);
            return this._navigator.graphService.cacheBoundingBox$({ lat: sw[0], lon: sw[1] }, { lat: ne[0], lon: ne[1] });
        }), mergeMap((node_candidates) => {
            return this._navigator.graphService.filter$.pipe(first(), mergeMap((current_filter) => {
                const filtered_candidates = node_candidates.filter((node_candidate) => current_filter(node_candidate));
                return observableOf(filtered_candidates);
            }));
        }))
            .subscribe((nodes) => {
            //this._add_debug_nodes(nodes);
            /* for (const feature of local_node_layer.getSource().getFeatures()) {
                local_node_layer.getSource().removeFeature(feature);
            }

            for (const node of nodes) {
                local_node_layer.getSource().addFeature(
                    new Feature<Point>({
                        geometry: new Point([node.latLon.lon, node.latLon.lat]).transform("EPSG:4326", "EPSG:3857"),
                        campaignID: node.frame.campaignID,
                    })
                );
            } */
            /* const group_sequences = nodes.reduce((r, a) => {
                r[a.frame.stream] = [...(r[a.frame.stream] || []), a];
                return r;
            }, {});

            for (const sequence_key in group_sequences) {
                const sequence = group_sequences[sequence_key];
                sequence.sort((a, b) => a.frame.framenumber - b.frame.framenumber);

                const line_coords = sequence.map((x) => [x.latLon.lon, x.latLon.lat]);
                local_node_layer.getSource().addFeature(
                    new Feature({
                        geometry: new LineString(line_coords).transform("EPSG:4326", "EPSG:3857"),
                        campaignID: sequence[0].frame.campaignID,
                    })
                );
            } */
        });
    }
    _init_tooltips() {
        const tooltip_element = document.createElement("div");
        tooltip_element.className = "infra3dsdk-tooltip";
        const overlay = new Overlay({
            element: tooltip_element,
            autoPan: {
                animation: {
                    duration: 250,
                },
            },
        });
        this._map.addOverlay(overlay);
        this._tooltip_overlay = overlay;
    }
    _recenterMap(node) {
        if (this._map.getView().getProperties().center) {
            const current_pos = new Point([node.latLon.lon, node.latLon.lat]);
            current_pos.transform("EPSG:4326", this._map.getView().getProjection().getCode());
            const currentextent = this._map.getView().calculateExtent(this._map.getSize());
            const ht = (currentextent[3] - currentextent[1]) / 10;
            const wt = (currentextent[2] - currentextent[0]) / 10;
            const x = current_pos.getCoordinates()[0];
            const y = current_pos.getCoordinates()[1];
            if (x <= currentextent[0] + wt ||
                x >= currentextent[2] - wt ||
                y <= currentextent[1] + ht ||
                y >= currentextent[3] - ht) {
                this._map.getView().setCenter(current_pos.getCoordinates());
            }
        }
    }
    deactivate() {
        this._currentnodesubscription.unsubscribe();
        this._geolocationlayer.deactivate();
    }
    routesLayerSetVisible(visible) {
        this._routes_layer.setVisible(visible);
    }
    framesLayerSetVisible(visible) {
        this._frames_layer.setVisible(visible);
    }
    setLidarMode(on, on_selections_changed, on_fragment_highlighted, selectioncolor, highlightcolor) {
        this.routesLayerSetVisible(!on);
        this.framesLayerSetVisible(!on);
        if (on) {
            this._lidar_layer.activate(on_selections_changed, on_fragment_highlighted, selectioncolor, highlightcolor);
        }
        else {
            this._lidar_layer.deactivate();
        }
    }
    enableLayer(title) {
        if (!this._layerSwitcher) {
            throw new Error(`LayerSwitcher not initialized`);
        }
        const map = this._map;
        if (!map) {
            throw new Error(`Map not initialized`);
        }
        const layers = LayerSwitcher.getGroupsAndLayers(map, () => true);
        if (!layers.find((layer) => layer.get("title") === title)) {
            throw new Error(`Layer not found: ${title}`);
        }
        layers.forEach((layer) => {
            if (layer.get("title") === title) {
                layer.setVisible(true);
            }
        });
    }
    disableLayer(title) {
        if (!this._layerSwitcher) {
            throw new Error(`LayerSwitcher not initialized`);
        }
        const map = this._map;
        if (!map) {
            throw new Error(`Map not initialized`);
        }
        const layers = LayerSwitcher.getGroupsAndLayers(map, () => true);
        if (!layers.find((layer) => layer.get("title") === title)) {
            throw new Error(`Layer not found: ${title}`);
        }
        layers.forEach((layer) => {
            if (layer.get("title") === title) {
                layer.setVisible(false);
            }
        });
    }
    getAllBasemapLayers() {
        return this._map
            .getLayers()
            .getArray()
            .filter((layer) => {
            const type = layer.get("type");
            if (type === "base")
                return true;
        })
            .sort((a, b) => b.getZIndex() - a.getZIndex());
    }
    getAllOverlayLayers() {
        return this._map
            .getLayers()
            .getArray()
            .filter((layer) => {
            const type = layer.get("type");
            if (type === "overlay")
                return true;
        })
            .sort((a, b) => b.getZIndex() - a.getZIndex());
    }
}
//# sourceMappingURL=Map.js.map